import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  FileText,
  MessageSquareText,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, PageHeader, SectionHeading, formatDateTime, formatRelativeTime } from "../components/ui";
import { useWorkspace } from "../lib/workspace";

export function HomePage() {
  const { data, loading } = useWorkspace();

  if (loading && !data) return <HomeSkeleton />;
  if (!data) return <EmptyState title="Workspace unavailable" body="Refresh the page or sign in again." />;

  const reviewItems = [
    ...data.inbox
      .filter((item) => item.reviewStatus === "needs_review")
      .map((item) => ({ id: item.id, label: item.participantLabel, body: item.preview, type: "Message", to: "/inbox", time: item.updatedAt })),
    ...data.comments
      .filter((item) => item.status === "needs_review")
      .map((item) => ({ id: item.id, label: item.authorLabel, body: item.body, type: "Comment", to: "/comments", time: item.createdAt })),
  ];
  const scheduled = data.operations.filter((operation) =>
    ["pending", "retry_wait", "needs_review"].includes(operation.status),
  );
  const attention = data.operations.filter((operation) =>
    ["failed", "needs_verification"].includes(operation.status),
  );
  const connected = data.channels.filter((channel) => channel.status === "connected").length;

  return (
    <>
      <PageHeader
        eyebrow="Today"
        title="Good afternoon, DrTun"
        description="Review what needs your decision before anything is sent."
        actions={<Link className="button button--primary" to="/content"><FileText size={17} />Create content</Link>}
      />

      {data.previewMode ? (
        <div className="preview-callout">
          <div><AlertTriangle size={20} /><span><strong>Safe preview</strong>No live providers are connected and no action can send externally.</span></div>
          <Link to="/connections">View connection blockers <ArrowUpRight size={15} /></Link>
        </div>
      ) : null}

      <div className="metric-grid">
        <MetricCard icon={<MessageSquareText />} label="Needs review" value={reviewItems.length} detail="Messages and comments" tone="blue" />
        <MetricCard icon={<CalendarClock />} label="Scheduled" value={scheduled.length} detail="Dispatch times" tone="purple" />
        <MetricCard icon={<AlertTriangle />} label="Needs attention" value={attention.length} detail="Failed or uncertain" tone={attention.length ? "amber" : "neutral"} />
        <MetricCard icon={<CheckCircle2 />} label="Connected" value={`${connected}/${data.channels.length}`} detail="Social channels" tone="green" />
      </div>

      <div className="home-grid">
        <Card className="home-grid__review">
          <SectionHeading
            title="Needs your review"
            description="AI drafts stay here until you approve them."
            action={<Link className="text-button" to="/inbox">Open inbox <ArrowUpRight size={15} /></Link>}
          />
          {reviewItems.length ? (
            <div className="review-list">
              {reviewItems.slice(0, 5).map((item) => (
                <Link className="review-item" to={item.to} key={`${item.type}-${item.id}`}>
                  <span className="review-item__icon"><MessageSquareText size={18} /></span>
                  <span className="review-item__copy">
                    <span><strong>{item.label}</strong><Badge tone="blue">{item.type}</Badge></span>
                    <span lang="my">{item.body}</span>
                  </span>
                  <time>{formatRelativeTime(item.time)}</time>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState title="Review queue is clear" body="New messages and comments will appear here." />
          )}
        </Card>

        <Card>
          <SectionHeading title="Upcoming work" description={`Times shown in ${data.settings.timezone}.`} />
          {scheduled.length ? (
            <div className="timeline-list">
              {scheduled.slice(0, 4).map((operation) => (
                <div className="timeline-item" key={operation.id}>
                  <span className="timeline-dot" />
                  <div>
                    <strong>{operation.action === "publish_content" ? "Content dispatch" : "Reply"}</strong>
                    <p>{formatDateTime(operation.scheduledFor, data.settings.timezone)}</p>
                  </div>
                  <Badge tone={operation.status === "needs_review" ? "amber" : "neutral"}>
                    {operation.status.replaceAll("_", " ")}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Nothing scheduled" body="Approved content will appear here with its dispatch time." />
          )}
        </Card>

        <Card>
          <SectionHeading title="Recent activity" description="A clear record of important workspace actions." />
          <div className="activity-list">
            {data.activity.slice(0, 6).map((activity) => (
              <div className="activity-item" key={activity.id}>
                <span className={`activity-status activity-status--${activity.status}`} />
                <div><strong>{activity.summary}</strong><time>{formatRelativeTime(activity.createdAt)}</time></div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SectionHeading title="Setup progress" description="Complete these steps before the first live Facebook send." />
          <ol className="setup-list">
            <SetupStep done={Boolean(data.context.profile)} title="Add Business Context" to="/business-context" />
            <SetupStep done={data.context.products.length > 0} title="Add approved products" to="/business-context" />
            <SetupStep done={connected > 0} title="Connect a Facebook Page" to="/connections" />
            <SetupStep done={false} title="Verify a real approved send" to="/inbox" />
          </ol>
        </Card>
      </div>
    </>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  detail: string;
  tone: string;
}) {
  return (
    <Card className="metric-card">
      <span className={`metric-card__icon metric-card__icon--${tone}`}>{icon}</span>
      <div><p>{label}</p><strong>{value}</strong><span>{detail}</span></div>
    </Card>
  );
}

function SetupStep({ done, title, to }: { done: boolean; title: string; to: string }) {
  return (
    <li className={done ? "setup-step--done" : ""}>
      <span>{done ? <CheckCircle2 size={18} /> : null}</span>
      <strong>{title}</strong>
      <Link to={to}>{done ? "Review" : "Continue"}<ArrowUpRight size={14} /></Link>
    </li>
  );
}

function HomeSkeleton() {
  return <div className="skeleton-page"><div /><div className="metric-grid">{[1, 2, 3, 4].map((key) => <div key={key} />)}</div><div /></div>;
}
